import * as React from 'react';
import { flushSync } from 'react-dom';
import clsx from 'clsx';
import { AlertCircle, ChevronDown } from 'lucide-react';
import { DetectedPdfField } from '@/interfaces';
import {
	ClientSignFieldState,
	ClientSignFieldValue,
} from './hooks/useClientSignFields';
import {
	FloatingSettingsSuggestion,
	SettingsSuggestionPopover,
} from './SettingsSuggestionPopover';
import { TooltipComponent } from '@/components/common/tooltip';
import Image from 'next/image';
import { isChoicePlaceholder } from './lib/choicePlaceholder';
import {
	blockInvalidContactKey,
	contactFormatError,
	sanitizeContactInput,
} from './lib/contactFieldFormat';

export interface ClientSignFieldOverlayProps {
	field: DetectedPdfField;
	state?: ClientSignFieldState;
	left: number;
	top: number;
	width: number;
	height: number;
	onValueChange: (fieldId: string, value: ClientSignFieldValue) => void;
	onSignatureClick?: (fieldId: string) => void;
	onOverlayRef?: (fieldId: string, node: HTMLElement | null) => void;
	isMismatch?: boolean;
	settingsValue?: string;
	showSuggestion?: boolean;
	onApplySuggestion?: () => void;
	onFocusField?: (fieldId: string) => void;
	onBlurField?: (fieldId: string) => void;
	minOverlayHeight?: number;
}

const MIN_OVERLAY_HEIGHT = 24;

const FieldFrame: React.FC<{
	isMismatch?: boolean;
	isFormatError?: boolean;
	readOnly?: boolean;
}> = ({ isMismatch = false, isFormatError = false, readOnly = false }) => (
	<span
		aria-hidden
		className={clsx(
			'pointer-events-none absolute -inset-[3px] rounded-md transition-[border-color,background-color,box-shadow]',
			isFormatError
				? 'border-solid border-main-red border-2'
				: isMismatch
				? 'border-solid border-main-orange border-2'
				: readOnly
				? 'border-2 border-solid border-[#0D06161F] bg-[#0D06160A]'
				: 'group-focus-within:shadow-[0_0_0_3px_rgba(128,57,223,0.15)]'
		)}
	/>
);

interface DropdownOverlayProps {
	field: DetectedPdfField;
	label: string;
	stringValue: string;
	isMismatch: boolean;
	readOnly: boolean;
	widgetStyle: React.CSSProperties;
	onOverlayRef?: (fieldId: string, node: HTMLElement | null) => void;
	onValueChange: (fieldId: string, value: ClientSignFieldValue) => void;
	onFocusField?: (fieldId: string) => void;
	onBlurField?: (fieldId: string) => void;
	settingsValue?: string;
	showSuggestion?: boolean;
	onApplySuggestion?: () => void;
}

const DropdownOverlay: React.FC<DropdownOverlayProps> = ({
	field,
	label,
	stringValue,
	isMismatch,
	readOnly,
	widgetStyle,
	onOverlayRef,
	onValueChange,
	onFocusField,
	onBlurField,
	settingsValue,
	showSuggestion = false,
	onApplySuggestion,
}) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const containerRef = React.useRef<HTMLDivElement | null>(null);
	const suggestionRef = React.useRef<HTMLDivElement>(null);
	const options = field.options ?? [];
	const currentOption = options.find((option) => option.value === stringValue);
	const displayLabel =
		currentOption?.label ||
		currentOption?.value ||
		stringValue ||
		// label ||
		'Select...';

	const setRef = (node: HTMLDivElement | null) => {
		containerRef.current = node;
		onOverlayRef?.(field.id, node);
	};

	React.useEffect(() => {
		if (!isOpen) return undefined;

		const onPointerDown = (event: PointerEvent) => {
			const target = event.target as Node;
			if (
				containerRef.current?.contains(target) ||
				suggestionRef.current?.contains(target)
			) {
				return;
			}
			setIsOpen(false);
			onBlurField?.(field.id);
		};
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				setIsOpen(false);
				onBlurField?.(field.id);
			}
		};

		document.addEventListener('pointerdown', onPointerDown);
		document.addEventListener('keydown', onKeyDown);
		return () => {
			document.removeEventListener('pointerdown', onPointerDown);
			document.removeEventListener('keydown', onKeyDown);
		};
	}, [field.id, isOpen, onBlurField]);

	const selectOption = (nextValue: string) => {
		onValueChange(field.id, nextValue);
		setIsOpen(false);
		onBlurField?.(field.id);
	};

	const suggestionVisible = Boolean(
		(showSuggestion || isOpen) &&
			(stringValue.trim().length === 0 || isChoicePlaceholder(stringValue)) &&
			settingsValue
	);

	return (
		<div
			ref={setRef}
			className={clsx('group absolute', isOpen ? 'z-[80]' : 'z-10')}
			style={widgetStyle}
		>
			<button
				type="button"
				aria-label={label}
				aria-haspopup="listbox"
				aria-expanded={isOpen}
				disabled={readOnly}
				onClick={() => {
					const next = !isOpen;
					setIsOpen(next);
					if (next) onFocusField?.(field.id);
					else onBlurField?.(field.id);
				}}
				className={clsx(
					'flex h-full w-full items-center bg-white pl-1 pr-4 text-left text-[11px] text-dark-100 outline-none disabled:cursor-not-allowed',
					isMismatch && 'text-main-orange'
				)}
			>
				<span className="min-w-0 truncate capitalize">{displayLabel}</span>
			</button>
			<ChevronDown
				className="pointer-events-none absolute right-0.5 top-1/2 size-3.5 -translate-y-1/2 text-dark-100"
				aria-hidden
			/>
			<FieldFrame isMismatch={isMismatch} readOnly={readOnly} />
			{isOpen && (
				<div
					role="listbox"
					className="absolute left-0 top-full z-[90] mt-0.5 max-h-40 min-w-full overflow-y-auto rounded-md border border-dark-100/10 bg-white py-1 shadow-shadow2"
				>
					{/* <button
						type="button"
						role="option"
						aria-selected={stringValue === ''}
						className="block w-full capitalize truncate px-2 py-1 text-left text-[11px] text-gray-35 hover:bg-purple-25"
						onMouseDown={(event) => event.preventDefault()}
						onClick={() => selectOption('')}
					>
						{label || 'Select'}
					</button> */}
					{stringValue !== '' && !currentOption && (
						<button
							type="button"
							role="option"
							aria-selected
							className="block w-full truncate px-2 py-1 text-left text-[8px] lg:text-[11px] hover:bg-purple-25"
							onMouseDown={(event) => event.preventDefault()}
							onClick={() => selectOption(stringValue)}
						>
							{stringValue}
						</button>
					)}
					{options.map((option) => (
						<button
							key={`${option.value}-${option.label}`}
							type="button"
							role="option"
							aria-selected={option.value === stringValue}
							className={clsx(
								'block w-full truncate px-2 py-1 text-left text-[8px] lg:text-[11px] hover:bg-purple-25',
								option.value === stringValue && 'bg-purple-25'
							)}
							onMouseDown={(event) => event.preventDefault()}
							onClick={() => selectOption(option.value)}
						>
							{option.label || option.value}
						</button>
					))}
				</div>
			)}
			{suggestionVisible && settingsValue && (
				<FloatingSettingsSuggestion
					anchorRef={containerRef}
					popoverRef={suggestionRef}
					settingsValue={settingsValue}
					onApply={() => {
						onApplySuggestion?.();
						setIsOpen(false);
						onBlurField?.(field.id);
					}}
				/>
			)}
		</div>
	);
};

export const ClientSignFieldOverlay: React.FC<ClientSignFieldOverlayProps> = ({
	field,
	state,
	left,
	top,
	width,
	height,
	onValueChange,
	onSignatureClick,
	onOverlayRef,
	isMismatch = false,
	settingsValue,
	showSuggestion = false,
	onApplySuggestion,
	onFocusField,
	onBlurField,
	minOverlayHeight = MIN_OVERLAY_HEIGHT,
}) => {
	const [isEditingPainted, setIsEditingPainted] = React.useState(false);
	const [chromeLocked, setChromeLocked] = React.useState(true);
	const paintedInputRef = React.useRef<HTMLInputElement>(null);
	const chromeLockTimer = React.useRef<number | null>(null);
	const overlayHeight = Math.max(height, minOverlayHeight);
	const mobileTopNudge = minOverlayHeight < MIN_OVERLAY_HEIGHT ? 7 : 0;
	const overlayTop = top + (height - overlayHeight) / 2 - mobileTopNudge;
	const fieldType = state?.fieldType ?? field.fieldType;
	const readOnly = state?.readOnly ?? false;
	const value = state?.value ?? (fieldType === 'checkbox' ? false : '');
	const label = state?.label ?? field.label;
	const valueMatchesPainted =
		typeof state?.paintedValue === 'string' &&
		state.paintedValue.length > 0 &&
		value === state.paintedValue;

	React.useEffect(() => {
		if (isEditingPainted) paintedInputRef.current?.focus();
	}, [isEditingPainted]);

	React.useEffect(
		() => () => {
			if (chromeLockTimer.current !== null) {
				window.clearTimeout(chromeLockTimer.current);
			}
		},
		[]
	);

	const releaseChromeLock = () => {
		if (readOnly) return;
		if (chromeLockTimer.current !== null) {
			window.clearTimeout(chromeLockTimer.current);
			chromeLockTimer.current = null;
		}
		setChromeLocked(false);
	};

	const unlockChromeForKeyboard = (
		event: React.SyntheticEvent<HTMLInputElement>
	) => {
		if (readOnly) return;
		event.currentTarget.readOnly = false;
		releaseChromeLock();
		event.currentTarget.focus();
	};

	const engageChromeLock = () => {
		if (chromeLockTimer.current !== null) {
			window.clearTimeout(chromeLockTimer.current);
			chromeLockTimer.current = null;
		}
		setChromeLocked(true);
	};

	const setRef = (node: HTMLElement | null) => {
		onOverlayRef?.(field.id, node);
	};

	const sharedStyle: React.CSSProperties = {
		left,
		top: overlayTop,
		width,
		height: overlayHeight,
	};

	const widgetStyle: React.CSSProperties = {
		left,
		top,
		width,
		height,
	};

	if (fieldType === 'signature') {
		const hasSignature = typeof value === 'string' && value.length > 0;
		if (valueMatchesPainted) {
			return (
				<button
					type="button"
					ref={setRef}
					aria-label={label || 'Edit signature'}
					className="group absolute z-[1] cursor-pointer bg-transparent outline-none"
					style={sharedStyle}
					onPointerDown={(event) => {
						event.currentTarget.dataset.pressed = '1';
					}}
					onPointerCancel={(event) => {
						delete event.currentTarget.dataset.pressed;
					}}
					onClick={(event) => {
						if (event.currentTarget.dataset.pressed !== '1') return;
						delete event.currentTarget.dataset.pressed;
						onSignatureClick?.(field.id);
					}}
				>
					<FieldFrame readOnly={readOnly} />
				</button>
			);
		}
		const coversPaintedSignature = Boolean(state?.paintedValue) && hasSignature;
		return (
			<button
				type="button"
				ref={setRef}
				aria-label={label || 'Draw signature'}
				disabled={readOnly}
				onPointerDown={(event) => {
					event.currentTarget.dataset.pressed = '1';
				}}
				onPointerCancel={(event) => {
					delete event.currentTarget.dataset.pressed;
				}}
				onClick={(event) => {
					if (event.currentTarget.dataset.pressed !== '1') return;
					delete event.currentTarget.dataset.pressed;
					onSignatureClick?.(field.id);
				}}
				className={clsx(
					'group absolute z-[1] flex items-center justify-center px-1 outline-none',
					coversPaintedSignature && 'bg-white'
				)}
				style={
					coversPaintedSignature
						? {
								left: left - 6,
								top: overlayTop - 6,
								width: width + 12,
								height: overlayHeight + 12,
						  }
						: sharedStyle
				}
			>
				{hasSignature ? (
					<Image
						width={100}
						height={100}
						src={value as string}
						alt="Signature"
						unoptimized
						className="max-h-full max-w-full object-contain"
					/>
				) : (
					<span className="max-w-full truncate text-[8px] lg:text-[10.5px] text-[#ADB3BD]">
						{'Draw Signature'}
					</span>
				)}
				<FieldFrame readOnly={readOnly} />
			</button>
		);
	}

	if (fieldType === 'checkbox') {
		const checked = Boolean(value);
		const matchesPdfDefault = Boolean(field.defaultValue) && checked;
		return (
			<label
				ref={setRef}
				className="group absolute z-10 flex cursor-pointer items-center justify-center"
				style={widgetStyle}
			>
				<span className="sr-only">{label}</span>
				<input
					type="checkbox"
					checked={checked}
					disabled={readOnly}
					onChange={(event) => onValueChange(field.id, event.target.checked)}
					className="absolute inset-0 m-0 h-full w-full cursor-pointer appearance-none bg-transparent outline-none"
				/>
				{checked && !matchesPdfDefault && (
					<span
						className="z-10 pointer-events-none text-[8px] lg:text-[11px] font-bold leading-none text-dark-100"
						aria-hidden
					>
						✓
					</span>
				)}
				<FieldFrame readOnly={readOnly} />
			</label>
		);
	}

	if (fieldType === 'radio') {
		const exportValue = field.exportValue || label || 'true';
		const checked =
			typeof value === 'string' && value !== '' && value === exportValue;
		const radioSize = Math.max(10, Math.min(width, height, 14));

		return (
			<label
				ref={setRef}
				className="group absolute z-10 flex cursor-pointer items-center justify-center"
				style={widgetStyle}
			>
				<span className="sr-only">{label}</span>
				<input
					type="radio"
					name={field.fieldName}
					checked={checked}
					disabled={readOnly}
					onChange={() => onValueChange(field.id, exportValue)}
					className="absolute inset-0 z-10 m-0 h-full w-full cursor-pointer appearance-none bg-transparent rounded-full outline-none"
				/>
				<span
					className="pointer-events-none flex items-center justify-center z-10"
					style={{ width: radioSize, height: radioSize }}
					aria-hidden
				>
					{checked && (
						<span
							className="rounded-full bg-dark-100"
							style={{
								width: radioSize * 0.45,
								height: radioSize * 0.45,
							}}
						/>
					)}
				</span>
				<FieldFrame readOnly={readOnly} />
			</label>
		);
	}

	if (fieldType === 'dropdown' || fieldType === 'listbox') {
		return (
			<DropdownOverlay
				field={field}
				label={label}
				stringValue={typeof value === 'string' ? value : ''}
				isMismatch={isMismatch}
				readOnly={readOnly}
				widgetStyle={widgetStyle}
				onOverlayRef={onOverlayRef}
				onValueChange={onValueChange}
				onFocusField={onFocusField}
				onBlurField={onBlurField}
				settingsValue={settingsValue}
				showSuggestion={showSuggestion}
				onApplySuggestion={onApplySuggestion}
			/>
		);
	}

	const stringValue = typeof value === 'string' ? value : '';
	const dataType = state?.dataType;
	const formatError = readOnly
		? null
		: contactFormatError(dataType, stringValue);
	const isEmpty = stringValue.trim().length === 0;
	const suggestionVisible = Boolean(showSuggestion && isEmpty && settingsValue);
	const showPaintedPdf =
		valueMatchesPainted && !isEditingPainted && !isMismatch && !formatError;

	if (showPaintedPdf) {
		return (
			<button
				type="button"
				ref={setRef}
				aria-label={label}
				className="absolute z-10 cursor-text bg-transparent"
				style={widgetStyle}
				onClick={() => {
					flushSync(() => {
						setChromeLocked(false);
						setIsEditingPainted(true);
					});
					paintedInputRef.current?.focus();
					onFocusField?.(field.id);
				}}
				onFocus={() => setIsEditingPainted(true)}
			/>
		);
	}

	return (
		<div
			ref={setRef}
			className={clsx('absolute', suggestionVisible ? 'z-[80]' : 'z-10')}
			style={{ left, top: overlayTop, width }}
		>
			<div className="group relative">
				<input
					ref={paintedInputRef}
					type={dataType === 'PHONE' ? 'tel' : 'text'}
					inputMode={dataType === 'PHONE' ? 'numeric' : undefined}
					maxLength={
						dataType === 'PHONE' ? 11 : dataType === 'EMAIL' ? 40 : undefined
					}
					// lock autocomplete
					autoComplete="one-time-code"
					name={`widget-${field.pageIndex}-${Math.round(
						field.rect.x
					)}-${Math.round(field.rect.y)}`}
					aria-label={label}
					aria-invalid={isMismatch || Boolean(formatError)}
					placeholder={label}
					readOnly={readOnly || chromeLocked}
					value={stringValue}
					onTouchStart={unlockChromeForKeyboard}
					onTouchEnd={(event) => event.preventDefault()}
					onMouseDown={unlockChromeForKeyboard}
					onKeyDown={(event) => blockInvalidContactKey(dataType, event)}
					onPaste={(event) => {
						event.preventDefault();
						const pastedText = event.clipboardData.getData('text');
						onValueChange(field.id, sanitizeContactInput(dataType, pastedText));
					}}
					onChange={(event) =>
						onValueChange(
							field.id,
							sanitizeContactInput(dataType, event.target.value)
						)
					}
					onFocus={() => {
						setIsEditingPainted(true);
						onFocusField?.(field.id);
						releaseChromeLock();
					}}
					onBlur={() => {
						engageChromeLock();
						setIsEditingPainted(false);
						onBlurField?.(field.id);
					}}
					className={clsx(
						'w-full px-1 text-[11px] text-dark-100 focus:outline-none',
						readOnly ? 'cursor-default' : 'cursor-text',
						state?.paintedValue ? 'bg-white' : 'bg-transparent',
						formatError && 'text-main-red',
						!formatError && isMismatch && 'text-main-orange'
					)}
					style={{ height: overlayHeight, minHeight: overlayHeight }}
				/>
				<FieldFrame
					isMismatch={isMismatch}
					isFormatError={Boolean(formatError)}
					readOnly={readOnly}
				/>
				{isMismatch && !formatError && (
					<span className="group/alert absolute left-full top-1/2 z-20 ml-1.5 -translate-y-1/2 text-main-orange">
						<TooltipComponent
							label={
								<div className="flex flex-col gap-1">
									<p className="text-xs font-bold uppercase tracking-wide text-black">
										In your settings
									</p>
									<p className="text-xs font-normal normal-case tracking-normal text-black text-left">
										{settingsValue}
									</p>
								</div>
							}
							trigger={<AlertCircle className="size-3.5" aria-hidden />}
						/>
					</span>
				)}
			</div>
			{formatError && (
				<p className="mt-0.5 text-[10px] leading-tight text-main-red text-left ml-2.5">
					{formatError}
				</p>
			)}
			{isMismatch && !formatError && (
				<p className="mt-0.5 text-[10px] leading-tight text-main-orange text-left ml-2.5">
					{`Doesn't match your settings`}
				</p>
			)}
			{showSuggestion && isEmpty && settingsValue && (
				<SettingsSuggestionPopover
					settingsValue={settingsValue}
					onApply={() => onApplySuggestion?.()}
				/>
			)}
		</div>
	);
};

export default ClientSignFieldOverlay;
